import React from 'react';
import { UserComponent } from '@gettactic/components/src/components/WeeklyOffice/cells/UserCell';
import { OfficeStatusCell } from '@gettactic/components/src/components/WeeklyOffice/cells/OfficeStatusCell';
import { getWorkingLocationStatus } from '@gettactic/components/src/components/WeeklyOffice/cells/WorkingLocationPresenceIcon';
import { makeWeeklyTableId } from '@gettactic/components/src/components/WeeklyOffice/weekly-office-helpers';
import { TacticApiClient, AuthenticatedUser } from '@gettactic/api';
import { WeeklyTableSkeleton } from './WeeklyTableSkeleton';
import { useDashboardFilters } from '@/contexts/DashboardStateContext';
import { useAuthenticated, useMyAgenda } from '@gettactic/hooks';
import { format, startOfWeek } from 'date-fns';
import { useBreakpoint } from '@gettactic/helpers/src/hooks/useBreakpoint';

interface AuthenticatedUserRowProps {
  authenticatedUser: AuthenticatedUser;
  headers: string[];
  favsApi: TacticApiClient;
  columnCount: number;
}

export const AuthenticatedUserRow = ({
  authenticatedUser,
  headers,
  favsApi,
  columnCount
}: AuthenticatedUserRowProps) => {
  const { filters } = useDashboardFilters();
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint('md');
  const {
    userContext: { deviceTz, currentOffice }
  } = useAuthenticated();

  const firstMondayThisWeek = startOfWeek(new Date(filters.weekDate), {
    weekStartsOn: 1
  });
  const { data: myRow } = useMyAgenda({
    from: format(firstMondayThisWeek, 'yyyy-MM-dd'),
    tz: currentOffice?.time_zone ?? deviceTz
  });

  const currentOfficeId = authenticatedUser?.officeId;
  const userId = authenticatedUser?.user?.id;

  if (!myRow || !Object.keys(myRow?.agenda ?? {}).length) {
    return (
      <WeeklyTableSkeleton
        myUser={authenticatedUser?.user}
        columnCount={columnCount}
      />
    );
  }

  const days = myRow?.days ? Object.keys(myRow.days) : [];

  if (!days.length) {
    return (
      <WeeklyTableSkeleton
        myUser={authenticatedUser?.user}
        columnCount={columnCount}
      />
    );
  }

  const dayCellsCount = columnCount - 1;
  const limitedDays = days.slice(0, dayCellsCount);

  const cells = (
    <>
      {/* Day cells */}
      {limitedDays.map((dateStr: string, index: number) => {
        const dayData = myRow?.agenda?.[dateStr];
        let status = 'unknown';
        if (dayData) {
          status = getWorkingLocationStatus({
            assignedPolicyType: dayData?.policy?.type ?? null,
            filterOfficeId: filters.office ?? currentOfficeId,
            officeIdOrOtherLocation: currentOfficeId || '',
            locationIsAssigned: !!dayData,
            assignedLocation: dayData?.location,
            assignedOfficeId: dayData?.office_id,
            assignedResource: dayData?.schedules[0]?.resource
          });
        }

        return (
          <div
            key={makeWeeklyTableId(headers, dateStr, index)}
            className="m-auto flex h-full w-full items-center justify-center"
          >
            <div className="w-full">
              <OfficeStatusCell
                officeName={
                  dayData?.office_id
                    ? authenticatedUser.offices?.byId[dayData.office_id]?.name
                    : ''
                }
                officeId={dayData?.office_id || ''}
                status={status}
                // TBD: use target slot here
                resource={dayData?.schedules[0]?.resource}
                date={dateStr}
                startTime={dayData?.schedules[0]?.start}
                endTime={dayData?.schedules[0]?.end}
              />
            </div>
          </div>
        );
      })}
    </>
  );

  return (
    <React.Fragment key={`auth-user-${userId}`}>
      {/* Name cell */}
      <div
        key="auth-name-cell"
        className="m-auto flex h-full w-full items-center"
      >
        <div className="h-full w-full">
          <UserComponent
            hasAnyFav={false}
            user={authenticatedUser.user}
            favsApi={favsApi}
            isMe={true}
          />
        </div>
      </div>

      {isMd ? cells : <div className={'flex gap-1'}>{cells}</div>}
    </React.Fragment>
  );
};
